import React from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";

export default function TpLink() {
  return (
    <div className="min-h-screen bg-[#111111] text-white scroll-smooth">
      <Helmet>
        <title>TP-Link | Smart Home Solutions | SeguraTech</title>
        <meta
          name="description"
          content="SeguraTech partners with TP-Link to deliver reliable smart home solutions — from Tapo cameras and lighting to Matter-compatible automation and Wi-Fi systems."
        />
        <meta property="og:title" content="TP-Link Smart Home Solutions" />
        <meta
          property="og:description"
          content="Explore TP-Link’s Tapo smart home range — smart cameras, lighting, power control, and Matter-ready devices integrated by SeguraTech."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.seguratech.co.uk/tplink" />
        <meta property="og:image" content="/tplink-logo.png" />
      </Helmet>

      {/* Hero Section */}
      <section className="max-w-4xl mx-auto px-4 py-24 text-center">
        <h1 className="text-4xl md:text-6xl font-extrabold leading-tight tracking-tight text-[#C6A564] mb-6">
          TP-Link:{" "}
          <span className="text-white/90">Smart Living. Simplified.</span>
        </h1>
        <p className="text-lg text-white/80 mb-16">
          SeguraTech integrates TP-Link’s <strong>Tapo</strong> smart home
          ecosystem for effortless automation. From smart cameras to lighting,
          power, and Wi-Fi, TP-Link devices connect seamlessly into a unified
          control system.
        </p>
      </section>

      {/* Feature Boxes */}
      <section className="max-w-7xl mx-auto px-4 pb-24">
        <div className="grid md:grid-cols-4 gap-8 text-center">

          {/* 1. Cameras */}
          <Link
            to="/tplink/cameras"
            className="border border-[#C6A564] rounded-2xl p-8 bg-[#1a1a1a] hover:bg-[#222222] transition block"
          >
            <h3 className="text-2xl font-semibold text-[#C6A564] mb-3">
              Tapo Cameras
            </h3>
            <p className="text-white/80">
              Smart indoor and outdoor cameras offering motion alerts, HD
              footage, and remote access via the Tapo app.
            </p>
          </Link>

          {/* 2. Smart Lighting */}
          <Link
            to="/tplink/lighting"
            className="border border-[#C6A564] rounded-2xl p-8 bg-[#1a1a1a] hover:bg-[#222222] transition block"
          >
            <h3 className="text-2xl font-semibold text-[#C6A564] mb-3">
              Tapo Smart Lighting
            </h3>
            <p className="text-white/80">
              Adjustable brightness, colour temperature, and schedules — light
              up your home with comfort and style.
            </p>
          </Link>

          {/* 3. Smart Power / Energy */}
          <Link
            to="/tplink/power"
            className="border border-[#C6A564] rounded-2xl p-8 bg-[#1a1a1a] hover:bg-[#222222] transition block"
          >
            <h3 className="text-2xl font-semibold text-[#C6A564] mb-3">
              Tapo Smart Power
            </h3>
            <p className="text-white/80">
              Control sockets and energy use remotely. Reduce consumption and
              manage devices from anywhere.
            </p>
          </Link>

          {/* 4. Deco Mesh Wi-Fi */}
          <Link
            to="/tplink/deco"
            className="border border-[#C6A564] rounded-2xl p-8 bg-[#1a1a1a] hover:bg-[#222222] transition block"
          >
            <h3 className="text-2xl font-semibold text-[#C6A564] mb-3">
              Tapo Deco Mesh Wi-Fi
            </h3>
            <p className="text-white/80">
              Seamless Wi-Fi 6/7 mesh coverage for every corner of your home —
              fast, secure, and reliable.
            </p>
          </Link>

          {/* 5. Alexa & Google Assistant */}
          <Link
            to="/tplink/alexa-google"
            className="border border-[#C6A564] rounded-2xl p-8 bg-[#1a1a1a] hover:bg-[#222222] transition block"
          >
            <h3 className="text-2xl font-semibold text-[#C6A564] mb-3">
              Alexa & Google Assistant
            </h3>
            <p className="text-white/80">
              Hands-free voice control for all TP-Link devices with Alexa and
              Google Home integration.
            </p>
          </Link>

          {/* 6. Matter Tech */}
          <Link to="/tplink/matter"
            className="border border-[#C6A564] rounded-2xl p-8 bg-[#1a1a1a] hover:bg-[#222222] transition block"
          >
            <h3 className="text-2xl font-semibold text-[#C6A564] mb-3">
              Matter Tech
            </h3>
            <p className="text-white/80">
              Universal compatibility with Apple, Google, and Amazon ecosystems
              through the Matter protocol. Simplified connectivity for every
              smart device.
           </p>
          </Link>

          {/* 7. Tapo Smart App */}
          <Link to="/tplink/app"
            className="border border-[#C6A564] rounded-2xl p-8 bg-[#1a1a1a] hover:bg-[#222222] transition block"
          >
            <h3 className="text-2xl font-semibold text-[#C6A564] mb-3">
              Tapo Smart App
            </h3>
            <p className="text-white/80">
              Manage all TP-Link devices from one simple app — instant setup,
              remote control, and automation from anywhere.
            </p>
          </Link>

          {/* 8. Smart Hubs */}
          <Link
            to="/tplink/smarthubs"
            className="border border-[#C6A564] rounded-2xl p-8 bg-[#1a1a1a] hover:bg-[#222222] transition block"
          >
            <h3 className="text-2xl font-semibold text-[#C6A564] mb-3">
              TP-Link Smart Hubs
            </h3>
            <p className="text-white/80">
              The heart of your smart home — H100, H200, and H500 hubs connect
              and coordinate your devices effortlessly.
            </p>
          </Link>

        </div>
      </section>

      {/* Back Link */}
      <div className="text-center pb-16">
        <Link
          to="/smarthome"
          className="text-[#C6A564] hover:text-[#E7D7B0] transition underline"
        >
          ← Back to Smart Home Overview
        </Link>
      </div>
    </div>
  );
}
